import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Star, Upload, CheckCircle, Users, X, Check, ChevronsUpDown, Save, Loader2, ListTodo } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
// Context menu removed - using selection-based task creation button instead
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import RichTextEditor from '../components/ui/rich-text-editor';
import api from '../lib/api';
import { toast } from 'sonner';

const FormFill = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [perUserResponses, setPerUserResponses] = useState({}); // {userId: {fieldId: value}}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [isResubmit, setIsResubmit] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [filteredUsersByField, setFilteredUsersByField] = useState({}); // Users filtered by field's allowed_groups
  const [userSelectorOpen, setUserSelectorOpen] = useState({});
  const [selectedReceivers, setSelectedReceivers] = useState([]); // Track selected users for feedback
  const [hasDraft, setHasDraft] = useState(false);
  
  // Task creation from selected text
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [creatingTask, setCreatingTask] = useState(false);

  useEffect(() => {
    fetchForm();
    fetchOrgUsers();
    fetchWorkspaces();
  }, [formId]);

  // Fetch filtered users for user_selector fields with allowed_groups
  const fetchFilteredUsersForField = async (fieldId, groupIds) => {
    try {
      const groupIdsParam = groupIds.join(',');
      const response = await api.get(`/users/all?exclude_self=true&group_ids=${groupIdsParam}`);
      setFilteredUsersByField(prev => ({
        ...prev,
        [fieldId]: response.data.users || []
      }));
    } catch (error) {
      console.error('Failed to fetch filtered users:', error);
    }
  };

  // When form loads, check for user_selector fields with allowed_groups and fetch filtered users
  useEffect(() => {
    if (form && form.fields) {
      form.fields.forEach(field => {
        if (field.field_type === 'user_selector') {
          const allowedGroups = field.user_selector_config?.allowed_groups || [];
          if (allowedGroups.length > 0) {
            fetchFilteredUsersForField(field.field_id, allowedGroups);
          }
        }
      });
    }
  }, [form]);

  const fetchOrgUsers = async () => {
    try {
      const response = await api.get('/users/all?exclude_self=true');
      setOrgUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch org users:', error);
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const response = await api.get('/workspaces');
      const allWorkspaces = response.data.workspaces || [];
      // Include personal workspace and member workspaces
      setWorkspaces(allWorkspaces);
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    }
  };

  const saveDraft = async (silent = false) => {
    // Don't save if form is not loaded or already submitted
    if (!form || submitted) return;
    
    setSavingDraft(true);
    try {
      await api.post(`/forms/${formId}/draft`, {
        responses,
        per_user_responses: perUserResponses,
        selected_receivers: selectedReceivers
      });
      setHasDraft(true);
      if (!silent) {
        toast.success('Draft saved successfully');
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSavingDraft(false);
    }
  };

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!form || submitted || loading) return;
    
    const autoSaveInterval = setInterval(() => {
      // Only auto-save if there are responses to save
      const hasResponses = Object.values(responses).some(v => 
        v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)
      );
      if (hasResponses) {
        saveDraft(true); // Silent save
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [form, submitted, loading, responses, perUserResponses, selectedReceivers, formId]);

  // Save draft when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!form || submitted) return;
      
      const hasResponses = Object.values(responses).some(v => 
        v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)
      );
      
      if (hasResponses) {
        // Try to save draft synchronously using sendBeacon
        const draftData = {
          responses,
          per_user_responses: perUserResponses,
          selected_receivers: selectedReceivers
        };
        
        const token = localStorage.getItem('token');
        const blob = new Blob([JSON.stringify(draftData)], { type: 'application/json' });
        
        // Use sendBeacon for reliable saving on page unload
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/forms/${formId}/draft`,
          blob
        );
      }
    };

    // Also save on visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && form && !submitted) {
        const hasResponses = Object.values(responses).some(v => 
          v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)
        );
        if (hasResponses) {
          saveDraft(true);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [form, submitted, responses, perUserResponses, selectedReceivers, formId]);

  const deleteDraft = async () => {
    try {
      await api.delete(`/forms/${formId}/draft`);
      setHasDraft(false);
    } catch (error) {
      // Ignore
    }
  };

  // Handle text selection to create task from selected text
  const handleTextSelection = useCallback(() => {
    // Small delay to ensure selection is complete
    setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (text && text.length > 0) {
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    }, 10);
  }, []);

  // Listen for selection changes globally when on this page
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (text && text.length > 0) {
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    };
    
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  const openCreateTaskDialog = () => {
    if (selectedText) {
      setTaskTitle(selectedText.substring(0, 100)); // Limit title length
      setTaskDescription(selectedText.length > 100 ? selectedText : '');
      setTaskDueDate('');
      setSelectedWorkspace('');
      setSelectedProject('');
      setCreateTaskOpen(true);
    }
  };

  const createTaskFromText = async () => {
    if (!taskTitle.trim()) {
      toast.error('Task title is required');
      return;
    }
    if (!selectedWorkspace) {
      toast.error('Please select a workspace');
      return;
    }

    setCreatingTask(true);
    try {
      await api.post('/tasks', {
        title: taskTitle,
        description: taskDescription,
        priority: 'medium',
        status: 'pending',
        workspace_id: selectedWorkspace,
        project_id: selectedProject || null,
        due_date: taskDueDate || null
      });
      toast.success('Task created successfully');
      setCreateTaskOpen(false);
      setSelectedText('');
      setTaskTitle('');
      setTaskDescription('');
      setTaskDueDate('');
      setSelectedWorkspace('');
      setSelectedProject('');
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const fetchForm = async () => {
    try {
      const response = await api.get(`/forms/${formId}`);
      const formData = response.data;
      
      if (formData.status !== 'active') {
        toast.error('This form is not accepting responses');
        navigate('/forms');
        return;
      }
      
      setForm(formData);
      
      // Check if user has an existing submission that was sent back
      try {
        const subResponse = await api.get(`/forms/${formId}/submissions`);
        const allSubmissions = subResponse.data.submissions || [];
        
        // For feedback forms, allow multiple submissions (for different receivers)
        // Only block if standard form with existing submission
        const isFeedbackForm = formData.form_type === 'feedback';
        
        // Look for a sent_back submission (current user's only)
        const sentBackSubmission = allSubmissions.find(
          s => s.submitted_by === user?.user_id && s.status === 'sent_back'
        );
        
        if (sentBackSubmission) {
          setExistingSubmission(sentBackSubmission);
          setIsResubmit(true);
          
          // Check if user has a saved draft (their edits) for this sent-back form
          try {
            const draftResponse = await api.get(`/forms/${formId}/draft`);
            if (draftResponse.data.draft) {
              const draft = draftResponse.data.draft;
              setResponses(draft.responses || {});
              setPerUserResponses(draft.per_user_responses || {});
              setSelectedReceivers(draft.selected_receivers || []);
              setHasDraft(true);
              toast.info('Draft loaded. Continue where you left off.');
              return;
            }
          } catch (e) {
            // No draft exists, use original submission responses
          }
          
          // No draft — pre-fill with original sent-back submission responses
          setResponses(sentBackSubmission.responses || {});
          return;
        }
        
        // For standard forms, check if current user already submitted
        if (!isFeedbackForm) {
          const existingSub = allSubmissions.find(
            s => s.submitted_by === user?.user_id && s.status !== 'sent_back'
          );
          if (existingSub) {
            setExistingSubmission(existingSub);
            setSubmitted(true);
            return;
          }
        }
        // For feedback forms, allow submitting for new receivers
      } catch (e) {
        // No existing submission, that's fine
      }
      
      // Try to load draft first, otherwise initialize empty responses
      try {
        const draftResponse = await api.get(`/forms/${formId}/draft`);
        if (draftResponse.data.draft) {
          const draft = draftResponse.data.draft;
          setResponses(draft.responses || {});
          setPerUserResponses(draft.per_user_responses || {});
          setSelectedReceivers(draft.selected_receivers || []);
          setHasDraft(true);
          toast.info('Draft loaded. Continue where you left off.');
          return;
        }
      } catch (e) {
        // No draft exists, continue to initialize empty responses
      }
      
      // Initialize responses with empty values for new submission (no draft found)
      const initialResponses = {};
      (formData.fields || []).forEach(field => {
        if (field.field_type === 'checkbox' || field.field_type === 'multiselect') {
          initialResponses[field.field_id] = [];
        } else if (field.field_type === 'user_selector' && field.user_selector_config?.multi_select) {
          initialResponses[field.field_id] = [];
        } else {
          initialResponses[field.field_id] = '';
        }
      });
      setResponses(initialResponses);
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const toggleCheckboxOption = (fieldId, optionValue) => {
    const currentValues = responses[fieldId] || [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter(v => v !== optionValue)
      : [...currentValues, optionValue];
    updateResponse(fieldId, newValues);
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    const isFeedbackForm = form?.form_type === 'feedback';
    const userSelectorField = (form?.fields || []).find(f => f.field_type === 'user_selector');
    
    (form?.fields || []).forEach(field => {
      if (field.required) {
        // For feedback forms with per-user responses, validate per-user fields
        if (isFeedbackForm && userSelectorField && field.field_type !== 'user_selector' && selectedReceivers.length > 0) {
          selectedReceivers.forEach(userId => {
            const value = perUserResponses[userId]?.[field.field_id];
            const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
            if (isEmpty) {
              newErrors[`${userId}_${field.field_id}`] = 'This field is required';
              isValid = false;
            }
          });
        } else {
          const value = responses[field.field_id];
          const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
          if (isEmpty) {
            newErrors[field.field_id] = 'This field is required';
            isValid = false;
          }
        }
      }
    });
    
    // Validate at least one user selected for feedback forms
    if (isFeedbackForm && userSelectorField && selectedReceivers.length === 0) {
      newErrors[userSelectorField.field_id] = 'Please select at least one recipient';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const isFeedbackForm = form?.form_type === 'feedback';
      const userSelectorField = (form?.fields || []).find(f => f.field_type === 'user_selector');
      
      if (isResubmit && existingSubmission) {
        // Resubmit existing sent-back submission
        await api.put(`/forms/${formId}/submissions/${existingSubmission.submission_id}/resubmit`, {
          responses
        });
        toast.success('Form resubmitted successfully!');
      } else if (isFeedbackForm && userSelectorField && selectedReceivers.length > 0) {
        // Feedback form with per-user submissions
        const submissions = [];
        for (const userId of selectedReceivers) {
          const userResponses = {
            ...responses,
            [userSelectorField.field_id]: userId, // Single user per submission
            ...(perUserResponses[userId] || {})
          };
          submissions.push({ receiver_id: userId, responses: userResponses });
        }
        
        // Submit each feedback separately
        for (const sub of submissions) {
          await api.post(`/forms/${formId}/submissions`, {
            responses: sub.responses,
            receiver_ids: [sub.receiver_id]
          });
        }
        toast.success(`Feedback submitted for ${submissions.length} user(s)!`);
      } else {
        // Standard form submission
        await api.post(`/forms/${formId}/submissions`, {
          responses
        });
        toast.success('Form submitted successfully!');
      }
      // Delete draft after successful submission
      await deleteDraft();
      setSubmitted(true);
      setIsResubmit(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = responses[field.field_id];
    const error = errors[field.field_id];
    
    const fieldWrapper = (children) => (
      <div key={field.field_id} className="space-y-2" data-testid={`field-${field.field_id}`}>
        <Label className="flex items-center gap-1">
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
        </Label>
        {field.help_text && (
          <p className="text-sm text-muted-foreground">{field.help_text}</p>
        )}
        {children}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );

    switch (field.field_type) {
      case 'text':
        return fieldWrapper(
          <Input
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'textarea':
        return fieldWrapper(
          <Textarea
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`${error ? 'border-red-500' : ''} min-h-[100px] resize-y`}
            style={{ height: 'auto', minHeight: '100px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onMouseUp={handleTextSelection}
          />
        );

      case 'richtext':
        return fieldWrapper(
          <div onMouseUp={handleTextSelection}>
            <RichTextEditor
              value={value || ''}
              onChange={(html) => updateResponse(field.field_id, html)}
              placeholder={field.placeholder || 'Start typing...'}
              minHeight="150px"
              className={error ? 'border-red-500' : ''}
            />
          </div>
        );

      case 'number':
        return fieldWrapper(
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            min={field.min_value}
            max={field.max_value}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'email':
        return fieldWrapper(
          <Input
            type="email"
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            placeholder={field.placeholder || 'email@example.com'}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'date':
        return fieldWrapper(
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'time':
        return fieldWrapper(
          <Input
            type="time"
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'select':
        return fieldWrapper(
          <Select value={value || ''} onValueChange={(v) => updateResponse(field.field_id, v)}>
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={field.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => (
                <SelectItem key={opt.option_id} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect':
        return fieldWrapper(
          <div className="space-y-2">
            {(field.options || []).map(opt => (
              <div key={opt.option_id} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.field_id}-${opt.option_id}`}
                  checked={(value || []).includes(opt.value)}
                  onCheckedChange={() => toggleCheckboxOption(field.field_id, opt.value)}
                />
                <Label htmlFor={`${field.field_id}-${opt.option_id}`} className="font-normal">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        );

      case 'radio':
        return fieldWrapper(
          <RadioGroup value={value || ''} onValueChange={(v) => updateResponse(field.field_id, v)}>
            {(field.options || []).map(opt => (
              <div key={opt.option_id} className="flex items-center gap-2">
                <RadioGroupItem value={opt.value} id={`${field.field_id}-${opt.option_id}`} />
                <Label htmlFor={`${field.field_id}-${opt.option_id}`} className="font-normal">
                  {opt.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return fieldWrapper(
          <div className="space-y-2">
            {(field.options || []).map(opt => (
              <div key={opt.option_id} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.field_id}-${opt.option_id}`}
                  checked={(value || []).includes(opt.value)}
                  onCheckedChange={() => toggleCheckboxOption(field.field_id, opt.value)}
                />
                <Label htmlFor={`${field.field_id}-${opt.option_id}`} className="font-normal">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        );

      case 'rating':
        const maxRating = field.max_value || 5;
        const currentRating = parseInt(value) || 0;
        return fieldWrapper(
          <div className="flex gap-1">
            {Array.from({ length: maxRating }, (_, i) => i + 1).map(star => (
              <button
                key={star}
                type="button"
                onClick={() => updateResponse(field.field_id, star.toString())}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= currentRating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        );

      case 'file':
        return fieldWrapper(
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              File upload coming soon
            </p>
          </div>
        );

      case 'user_selector':
        const config = field.user_selector_config || {};
        const isMulti = config.multi_select;
        const selectedUsers = isMulti ? (value || []) : (value ? [value] : []);
        
        // Use filtered users if allowed_groups is configured, otherwise use all org users
        const allowedGroups = config.allowed_groups || [];
        const usersForField = allowedGroups.length > 0 
          ? (filteredUsersByField[field.field_id] || [])
          : orgUsers;
        
        const availableUsers = usersForField.filter(u => !selectedUsers.includes(u.user_id));
        
        const toggleUser = (userId) => {
          let newSelection;
          if (isMulti) {
            const current = value || [];
            if (current.includes(userId)) {
              newSelection = current.filter(id => id !== userId);
            } else {
              newSelection = [...current, userId];
            }
            updateResponse(field.field_id, newSelection);
            // Update selected receivers for feedback forms
            if (form?.form_type === 'feedback') {
              setSelectedReceivers(newSelection);
            }
          } else {
            updateResponse(field.field_id, userId);
            setUserSelectorOpen({ ...userSelectorOpen, [field.field_id]: false });
            // Update selected receivers for single select feedback forms
            if (form?.form_type === 'feedback') {
              setSelectedReceivers([userId]);
            }
          }
        };

        const selectAll = () => {
          if (isMulti && config.allow_select_all) {
            const allUserIds = usersForField.map(u => u.user_id);
            updateResponse(field.field_id, allUserIds);
            if (form?.form_type === 'feedback') {
              setSelectedReceivers(allUserIds);
            }
          }
        };

        const clearAll = () => {
          updateResponse(field.field_id, isMulti ? [] : '');
          if (form?.form_type === 'feedback') {
            setSelectedReceivers([]);
            setPerUserResponses({});
          }
        };

        return fieldWrapper(
          <div className="space-y-2">
            <Popover 
              open={userSelectorOpen[field.field_id]} 
              onOpenChange={(open) => setUserSelectorOpen({ ...userSelectorOpen, [field.field_id]: open })}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={`w-full justify-between ${error ? 'border-red-500' : ''}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Users className="w-4 h-4" />
                    {selectedUsers.length === 0 
                      ? (field.placeholder || 'Select user(s)...')
                      : `${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} selected`
                    }
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search users..." />
                  <CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                    {isMulti && config.allow_select_all && (
                      <CommandGroup>
                        <CommandItem onSelect={selectAll} className="text-[#4A9CC7]">
                          <Check className="mr-2 h-4 w-4 opacity-0" />
                          Select All
                        </CommandItem>
                      </CommandGroup>
                    )}
                    <CommandGroup heading="Users">
                      {usersForField.map((user) => (
                        <CommandItem
                          key={user.user_id}
                          value={user.name + ' ' + user.email}
                          onSelect={() => toggleUser(user.user_id)}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              selectedUsers.includes(user.user_id) ? 'opacity-100' : 'opacity-0'
                            }`}
                          />
                          <div className="flex flex-col">
                            <span>{user.name}</span>
                            <span className="text-xs text-muted-foreground">{user.email}</span>
                          </div>
                        </CommandItem>
                      ))}
                      {usersForField.length === 0 && (
                        <CommandItem disabled className="text-muted-foreground">
                          No users available
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            
            {/* Selected users display */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(userId => {
                  const user = usersForField.find(u => u.user_id === userId) || orgUsers.find(u => u.user_id === userId);
                  return user ? (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      {user.name}
                      <button
                        type="button"
                        onClick={() => toggleUser(userId)}
                        className="ml-1 hover:bg-muted rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ) : null;
                })}
                {selectedUsers.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={clearAll} className="h-6 text-xs">
                    Clear all
                  </Button>
                )}
              </div>
            )}
          </div>
        );

      default:
        return fieldWrapper(
          <Input
            value={value || ''}
            onChange={(e) => updateResponse(field.field_id, e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  if (loading) {
    return (
      <AppLayout>
      <div className="p-6 flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading form...</p>
      </div>
      </AppLayout>
    );
  }

  if (submitted) {
    return (
      <AppLayout>
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Thank you!</h2>
            <p className="text-muted-foreground mb-6">
              Your response has been submitted successfully.
            </p>
            <Button onClick={() => navigate('/forms')} variant="outline">
              Back to Forms
            </Button>
          </CardContent>
        </Card>
      </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forms')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-semibold">{form?.name}</h1>
            <p className="text-sm text-muted-foreground">{form?.workspace_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {isResubmit && existingSubmission && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-amber-800">Sent Back for Changes</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Your previous submission was sent back. Please review the comments and make the necessary changes.
                </p>
                {existingSubmission.review_comments && existingSubmission.review_comments.length > 0 && (
                  <div className="mt-2 p-2 bg-white rounded border border-amber-200">
                    <p className="text-xs text-muted-foreground mb-1">Latest comment:</p>
                    <p className="text-sm">{existingSubmission.review_comments[existingSubmission.review_comments.length - 1].comment || 'No comment provided'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <Card>
          <CardHeader>
            <CardTitle>{form?.name}</CardTitle>
            {form?.description && (
              <CardDescription>{form.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* For feedback forms, render user selector first, then per-user fields */}
            {form?.form_type === 'feedback' ? (
              <>
                {/* User Selector Field */}
                {(form?.fields || [])
                  .filter(f => f.field_type === 'user_selector')
                  .map(field => renderField(field))}
                
                {/* Per-User Feedback Sections */}
                {selectedReceivers.length > 0 && (
                  <div className="space-y-6 mt-6">
                    <div className="border-t pt-4">
                      <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        Individual Feedback
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Provide specific feedback for each selected person
                      </p>
                    </div>
                    
                    {selectedReceivers.map((userId, index) => {
                      const user = orgUsers.find(u => u.user_id === userId);
                      if (!user) return null;
                      
                      return (
                        <Card key={userId} className="border-purple-200 dark:border-purple-800 bg-purple-50/30 dark:bg-purple-950/20">
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-purple-600 dark:text-purple-300 font-medium">
                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <CardTitle className="text-base">{user.name}</CardTitle>
                                <CardDescription className="text-xs">{user.email}</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Render other fields for this user */}
                            {(form?.fields || [])
                              .filter(f => f.field_type !== 'user_selector')
                              .sort((a, b) => a.order - b.order)
                              .map(field => {
                                const perUserValue = perUserResponses[userId]?.[field.field_id] || '';
                                const perUserError = errors[`${userId}_${field.field_id}`];
                                
                                return (
                                  <div key={`${userId}_${field.field_id}`} className="space-y-2">
                                    <Label className={perUserError ? 'text-red-500' : ''}>
                                      {field.label}
                                      {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </Label>
                                    {field.field_type === 'textarea' ? (
                                      <Textarea
                                        value={perUserValue}
                                        onChange={(e) => {
                                          setPerUserResponses(prev => ({
                                            ...prev,
                                            [userId]: {
                                              ...(prev[userId] || {}),
                                              [field.field_id]: e.target.value
                                            }
                                          }));
                                          if (errors[`${userId}_${field.field_id}`]) {
                                            setErrors(prev => ({ ...prev, [`${userId}_${field.field_id}`]: null }));
                                          }
                                        }}
                                        placeholder={field.placeholder || `Enter feedback for ${user.name}...`}
                                        className={`min-h-[100px] ${perUserError ? 'border-red-500' : ''}`}
                                      />
                                    ) : field.field_type === 'rating' ? (
                                      <div className="flex gap-1">
                                        {Array.from({ length: field.max_value || 5 }, (_, i) => i + 1).map(star => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => {
                                              setPerUserResponses(prev => ({
                                                ...prev,
                                                [userId]: {
                                                  ...(prev[userId] || {}),
                                                  [field.field_id]: star.toString()
                                                }
                                              }));
                                            }}
                                            className="p-1 hover:scale-110 transition-transform"
                                          >
                                            <Star
                                              className={`w-6 h-6 ${
                                                star <= parseInt(perUserValue || 0)
                                                  ? 'fill-yellow-400 text-yellow-400'
                                                  : 'text-gray-300'
                                              }`}
                                            />
                                          </button>
                                        ))}
                                      </div>
                                    ) : (
                                      <Input
                                        value={perUserValue}
                                        onChange={(e) => {
                                          setPerUserResponses(prev => ({
                                            ...prev,
                                            [userId]: {
                                              ...(prev[userId] || {}),
                                              [field.field_id]: e.target.value
                                            }
                                          }));
                                        }}
                                        placeholder={field.placeholder}
                                        className={perUserError ? 'border-red-500' : ''}
                                      />
                                    )}
                                    {perUserError && (
                                      <p className="text-xs text-red-500">{perUserError}</p>
                                    )}
                                    {field.help_text && (
                                      <p className="text-xs text-muted-foreground">{field.help_text}</p>
                                    )}
                                  </div>
                                );
                              })}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Standard form rendering */
              (form?.fields || [])
                .sort((a, b) => a.order - b.order)
                .map(field => renderField(field))
            )}
            
            <div className="pt-4 space-y-3">
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveDraft(false)}
                  disabled={savingDraft || submitting}
                  className="flex-1"
                  data-testid="save-draft-btn"
                >
                  {savingDraft ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Draft
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || savingDraft}
                  className="flex-1 bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  data-testid="submit-form-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {isResubmit ? 'Resubmit' : 'Submit'}
                    </>
                  )}
                </Button>
              </div>
              {hasDraft && (
                <p className="text-xs text-center text-muted-foreground">
                  Draft auto-saved. You can close this page and continue later.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Floating Create Task Button - appears when text is selected */}
        {selectedText && (
          <div className="fixed bottom-24 right-6 z-50">
            <Button
              onClick={openCreateTaskDialog}
              className="bg-[#4A9CC7] hover:bg-[#3d8ab5] shadow-lg"
              data-testid="create-task-from-selection-btn"
            >
              <ListTodo className="w-4 h-4 mr-2" />
              Create Task from Selection
            </Button>
          </div>
        )}

        {/* Create Task Dialog */}
        <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-[#4A9CC7]" />
                Create Task from Selected Text
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Task Title *</Label>
                <Input
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="Enter task title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder="Add task description..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Workspace *</Label>
                <Select 
                  value={selectedWorkspace || "_placeholder_"} 
                  onValueChange={(v) => { 
                    if (v !== "_placeholder_") setSelectedWorkspace(v); 
                    setSelectedProject(''); 
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.length === 0 ? (
                      <SelectItem value="_no_ws_" disabled>No workspaces found</SelectItem>
                    ) : (
                      workspaces.map((ws) => (
                        <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                          {ws.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedWorkspace && (
                <div className="space-y-2">
                  <Label>Project (optional)</Label>
                  <Select 
                    value={selectedProject || "_none_"} 
                    onValueChange={(v) => setSelectedProject(v === "_none_" ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">No project</SelectItem>
                      {(workspaces.find(w => w.workspace_id === selectedWorkspace)?.projects || []).map((p) => (
                        <SelectItem key={p.project_id} value={p.project_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input 
                  type="date" 
                  value={taskDueDate} 
                  onChange={(e) => setTaskDueDate(e.target.value)} 
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateTaskOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createTaskFromText} 
                disabled={creatingTask || !taskTitle.trim() || !selectedWorkspace}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
              >
                {creatingTask ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Task'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </AppLayout>
  );
};

export default FormFill;
